import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import { actionsKeys, cn, formatAmount, permissionActions } from "@/lib/utils";
import { setBudgetData } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  useDeleteBudgetMutation,
  useGetCommunities,
  useGetBudgetByCommunityData,
  useGetBomUsersList,
  useGetCommunityApprovalInfo,
  useRevisedBudgetMutation,
  useSubmitForApprovalMutation,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import {
  Check,
  Copy,
  Edit,
  FileText,
  History,
  MoreVertical,
  RotateCw,
  Trash2,
} from "lucide-react";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "@/components/Toast";
import { setCommunityData } from "@/redux/slices";

export const statusColor: any = {
  draft: "border-yellow-600 bg-yellow-100 text-yellow-600",
  rejected: "border-red-600 bg-red-100 text-red-600",
  archived: "border-red-600 bg-red-100 text-red-600",
  approved: "border-green-600 bg-green-100 text-green-600",
  pending_approval: "border-red-600 bg-red-100 text-red-600",
};

export const status = {
  draft: "Draft",
  rejected: "Rejected",
  approved: "Approved",
  pending_approval: "Pending Approval",
  archived: "Archived",
};

const useBudgetMgmt = () => {
  const [globalFilter, setGlobalFilter] = useState("");
  const isMobile = useIsMobile();
  const { id } = useParams();
  const location = useLocation();
  const isMasterDataRoute = location.pathname.toLowerCase().includes(routes.masterData);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [rowSelected, setRowSelected] = useState<any>(null);
  const [showCopyDialog, setShowCopyDialog] = useState<boolean>(false);
  const [openApprovalDialog, setOpenApprovalDialog] = useState<boolean>(false);
  const [openReviseDialog, setOpenReviseDialog] = useState<boolean>(false);
  const [openLogSheet, setOpenLogSheet] = useState<boolean>(false);
  const [communityKey, setCommunityKey] = useState(Math.random() * 2);
  const [openBudgetBreakdownDialog, setOpenBudgetBreakdownDialog] =
    useState<boolean>(false);
  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const communityId = (id || communityDetail?.communityId) as string;
  const accessPermission = useSelector(
    (state: RootState) => state.currentData.accessPermission
  );

  const { data: communityList, isLoading: isCommunityLoading } =
    useGetCommunities({});

  useEffect(() => {
    if (communityList?.data) {
      setCommunityKey(Math.random() * 3);
    }
  }, [communityList]);

  const { data: budgetData, isLoading: isBudgetDataLoading } =
    useGetBudgetByCommunityData({
      communityId,
      queryParams: {
        enabled: !!communityId,
      },
    });

  const { data: bomUsersList } = useGetBomUsersList({
    communityId,
    queryParams: {
      enabled: !!communityId,
    },
  });

  const { data: communityApprovalInfo } = useGetCommunityApprovalInfo({
    community_id: communityId,
    queryParams: {
      enabled: !!communityId,
    },
  });

  const { mutate: deleteInfo, isPending: isDeletePending } =
    useDeleteBudgetMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["get-budget-data", communityId],
        });
        setOpenDeleteDialog(false);
        setRowSelected(null);
        toast.success("Budget Deleted Successfully");
      },
    });

  const { mutate: submitApproval, isPending: isSubmitApprovalPending } =
    useSubmitForApprovalMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["get-budget-data", communityId],
        });
        setOpenApprovalDialog(false);
        setRowSelected(null);
        toast.success("Budget Submitted for Approval");
      },
    });

  const { mutate: revisedBudget, isPending: isRevisedBudgetPending } =
    useRevisedBudgetMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["get-budget-data", communityId],
        });
        setOpenReviseDialog(false);
        setRowSelected(null);
        toast.success("Budget Revised Successfully");
      },
    });
    
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const budgetPermission = useMemo(
    () =>
      accessPermission[actionsKeys.BUDGET?.toLowerCase()],
    [accessPermission]
  );

  const breadcrumbData = isMasterDataRoute ? undefined : [
    {
      link: `/${routes.cam}/${routes.communities}`,
      name: `Community - ${(communityDetail as any)?.name || ""}`,
    },
    {
      link: "",
      name: <span className="text-primary">{"Budget Management"}</span>,
    },
  ];

  // Define Table Columns
  const columns = [
    {
      accessorKey: "budgetId",
      header: "Budget ID",
      accessorFn: (row: any) => String(row.budgetId),
    },
    {
      accessorKey: "budgetName",
      header: "Budget Name",
      accessorFn: (row: any) => String(row?.budgetName || ""),
    },
    {
      accessorKey: "year",
      header: (
        <div>
          Financial <br /> Year
        </div>
      ),
      accessorFn: (row: any) => String(row.year),
    },
    {
      accessorKey: "versionNo",
      header: (
        <div>
          Version <br /> No.
        </div>
      ),
      accessorFn: (row: any) => String(row?.versionNo) || "",
    },
    {
      accessorKey: "totalAnnualBudget",
      header: (
        <div>
          Total <br /> Budget
        </div>
      ),
      cell: (row: any) => {
        return (
          <Button
            type="button"
            variant="link"
            className="w-fit p-0"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setRowSelected(row.row.original);
              setOpenBudgetBreakdownDialog(true);
            }}
          >
            {formatAmount(
              String(row.row.original?.totalExpenseByCategory?.Expense || "")
            )}
          </Button>
        );
      },
    },
    {
      accessorKey: "lineItemsCount",
      header: <div>Total Line Items</div>,
      accessorFn: (row: any) => String(row?.lineItemsCount || "0"),
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: (row: any) => {
        return (
          <Badge
            variant="outline"
            className={cn(
              statusColor[row.row.original.status?.toLowerCase()],
              "rounded-full px-4 py-2"
            )}
          >
            {(status as any)[row.row.original.status?.toLowerCase()]}
          </Badge>
        );
      },
    },
    {
      accessorKey: "updatedByName",
      header: (
        <div>
          Last <br /> Updated By
        </div>
      ),
      accessorFn: (row: any) => String(row?.updatedByName || ""),
    },
    {
      accessorKey: "updatedAt",
      header: (
        <div>
          Last <br /> Updated On
        </div>
      ),
      accessorFn: (row: any) =>
        moment(row?.updatedAt || "").format("MM/DD/YYYY"),
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row: any) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <MoreVertical className="w-5 h-5 cursor-pointer" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-0 shadow-md"
            >
              {row.row.original.status.toLowerCase() == "draft" && (
                <>
                  {budgetPermission[permissionActions.UPDATE] && (
                    <DropdownMenuItem
                      className="flex items-center gap-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        navigate(`${row.row.original.budgetId}/edit`);
                      }}
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </DropdownMenuItem>
                  )}

                  {budgetPermission[permissionActions.DELETE] && (
                    <DropdownMenuItem
                      className="flex items-center gap-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setRowSelected(row.row.original);
                        setOpenDeleteDialog(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </DropdownMenuItem>
                  )}
                </>
              )}
              {budgetPermission[permissionActions.CREATE] && (
                <DropdownMenuItem
                  className="flex items-center gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setRowSelected(row.row.original);
                    setShowCopyDialog(true);
                  }}
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </DropdownMenuItem>
              )}
              {budgetPermission[permissionActions.CREATE] &&
                budgetPermission[permissionActions.UPDATE] && (
                  <DropdownMenuItem
                    className="flex items-center gap-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      dispatch(setBudgetData(row.row.original));
                      navigate(
                        `${row.row.original.budgetId}/${routes.lineItems}`
                      );
                    }}
                  >
                    <FileText className="w-4 h-4" />
                    Line Items
                  </DropdownMenuItem>
                )}
              {row.row.original.status.toLowerCase() == "draft" &&
              row.row.original.versionNo == "0" &&
              budgetPermission[permissionActions.SUBMIT] ? (
                <DropdownMenuItem
                  className="flex items-center gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (row.row.original.lineItemsCount) {
                      if (!validateBudgetApproval()) return;
                      setRowSelected(row.row.original);
                      setOpenApprovalDialog(true);
                    } else {
                      toast.error(
                        "You can't submit this budget for approval because it has no line items."
                      );
                    }
                  }}
                >
                  <Check className="w-4 h-4" />
                  Submit For Approval
                </DropdownMenuItem>
              ) : (
                <></>
              )}
              {row.row.original.status.toLowerCase() == "approved" &&
              budgetPermission[permissionActions.UPDATE] ? (
                <DropdownMenuItem
                  disabled={isRevisedBudgetPending}
                  className="flex items-center gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                      setRowSelected(row.row.original);
                      setOpenReviseDialog(true);
                  }}
                >
                  <RotateCw className="w-4 h-4" />
                  Revise Budget
                </DropdownMenuItem>
              ) : (
                <></>
              )}
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setRowSelected(row.original);
                  setOpenLogSheet(true);
                }}
              >
                <History className="w-4 h-4" />
                View Log
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const onAdd = () => {
    navigate(`${routes.add}`);
  };

  const onRowClick = (row: any) => {
    navigate(`${row.original.budgetId}/view`);
  };

  const validateBudgetApproval = () => {
    const activeBomCount = (bomUsersList?.data || []).filter(
      (bom: any) => bom?.active
    ).length;
    const requiredApprovals = Number(
      communityApprovalInfo?.data?.budgetApprovals || 0
    );

    if (activeBomCount < requiredApprovals) {
      toast.error(
        "You can't submit this budget for approval because approvals are greater than active BM members."
      );
      return false;
    }

    return true;
  };

  const onDeleteConfirm = () => {
    deleteInfo({ communityId, budgetId: rowSelected?.budgetId });
  };

  const onSubmitApproval = () => {
    if (!validateBudgetApproval()) return;
    submitApproval({
      communityId,
      budgetId: rowSelected?.budgetId,
      reason: "Submitted for Approval",
    });
  };

  const onRevisedConfirm = () => {
    revisedBudget({
      communityId,
      budgetId: rowSelected?.budgetId,
    });
  }

  const onCommunityChange = (e: any) => {
    const details = communityList?.data?.find(
      (data: any) => data.communityId == e
    );
    dispatch(setCommunityData(details));
  };

  return {
    breadcrumbData,
    isMobile,
    onAdd,
    globalFilter,
    setGlobalFilter,
    onRowClick,
    columns,
    budgetData,
    isBudgetDataLoading,
    rowSelected,
    onDeleteConfirm,
    openDeleteDialog,
    setOpenDeleteDialog,
    isDeletePending,
    setShowCopyDialog,
    showCopyDialog,
    openApprovalDialog,
    setOpenApprovalDialog,
    onSubmitApproval,
    isSubmitApprovalPending,
    openBudgetBreakdownDialog,
    setOpenBudgetBreakdownDialog,
    budgetPermission,
    onRevisedConfirm,
    setOpenReviseDialog,
    openReviseDialog,
    openLogSheet,
    setOpenLogSheet,
    isMasterDataRoute,
    communityList,
    isCommunityLoading,
    communityId,
    onCommunityChange,
    communityKey
  };
};

export default useBudgetMgmt;
